import AutoLayout from "../../Layouts/AutoLayout/AutoLayout";
import FontText from "../../FontText/FontText";
import Icon from "../../Icon/Icon";
import React from "react";
import RippleEffect from "../../RippleEffect/RippleEffect";
import styled, { css } from "styled-components";
import {
  AutoLayoutAlignEnum,
  AutoLayoutDirectionEnum,
  AutoLayoutResizingEnum,
} from "../../Layouts/AutoLayout/AutoLayoutEnums";
import { ComponentStateEnum, ThemeColorEnum, ThemeIconEnum } from "../../Themes/ThemeEnums";
import { FontSizeEnum, FontTypeEnum } from "../../FontText/FontTextEnums";
import { ITheme } from "../../Themes/ThemeTypes";
import { RippleEffectShapeEnum } from "../../RippleEffect/RippleEffectShapeEnum";
import { useIsMobile } from "../../Layouts/AppLayout/IsMobileHook";
import { useSelector } from "react-redux";
import { AppCoreState } from "../../../../Redux/AppCoreReducer";

const ButtonAutoLayout = styled(AutoLayout)<{
  theme: ITheme;
  $isRail: boolean;
}>`
  ${(props) => {
    if (props.$isRail) {
      return css`
        height: 32px;
      `;
    } else {
      return css`
        height: 56px;
      `;
    }
  }};

  border-start-start-radius: 9999px;
  border-start-end-radius: 9999px;
  border-end-start-radius: 9999px;
  border-end-end-radius: 9999px;
  gap: 12px;
  white-space: nowrap;
  width: 100%;
  text-align: left;
  padding-left: 16px;
  transition: height ${(props) => props.theme.base.left_nav.transition_length}
    ${(props) => props.theme.base.motion.easing.standard};

  .font-text-drawer {
    transition:
      opacity ${(props) => props.theme.base.left_nav.transition_length}
        ${(props) => props.theme.base.motion.easing.standard},
      visibility ${(props) => props.theme.base.left_nav.transition_length}
        ${(props) => props.theme.base.motion.easing.standard};
    ${(props) => {
      if (props.$isRail) {
        return css`
          opacity: 0;
          visibility: hidden;
        `;
      } else {
        return css`
          opacity: 1;
          visibility: visible;
        `;
      }
    }};
  }

  @media (max-width: ${(props) => props.theme.base.responsive.mobile}px) {
    height: 56px;
    .font-text-drawer {
      opacity: 1;
      visibility: visible;
    }
  }
`;

const HoverDiv = styled.div<{
  theme: ITheme;
  $componentState?: ComponentStateEnum;
}>`
  cursor: pointer;
  outline: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  ${(props) => {
    if (props.$componentState === ComponentStateEnum.ENABLED) {
      return css`
        .button {
          background-color: rgb(${props.theme.colors.secondary_container});
        }
      `;
    } else {
      return css`
        &:hover {
          .button {
            background-color: ${props.theme.colors.on_surface_variant_opacity_0_08};
          }
        }
      `;
    }
  }};
`;

const DivRail = styled.div<{ theme: ITheme; $isRail: boolean }>`
  text-align: center;
  transition:
    opacity ${(props) => props.theme.base.left_nav.transition_length}
      ${(props) => props.theme.base.motion.easing.standard},
    visibility ${(props) => props.theme.base.left_nav.transition_length}
      ${(props) => props.theme.base.motion.easing.standard};
  ${(props) => {
    if (props.$isRail) {
      return css`
        opacity: 1;
        visibility: visible;
        height: auto;
        padding-top: 4px;
        padding-bottom: 12px;
      `;
    } else {
      return css`
        opacity: 0;
        visibility: hidden;
        height: 0px;
        padding-top: 0px;
        padding-bottom: 0px;
      `;
    }
  }};

  @media (max-width: ${(props) => props.theme.base.responsive.mobile}px) {
    display: none;
  }
`;

interface NavigationItemButtonProps {
  icon: ThemeIconEnum;

  /**
   * The label text for the button.
   */
  label: string;

  state?: ComponentStateEnum;

  isActive?: boolean;
}

const NavigationItemButton: React.FC<NavigationItemButtonProps> = ({
  icon,
  label,
  state,
  isActive,
}) => {
  const isAppLeftNavToggled = useSelector(
    (state: { core: AppCoreState }) => state.core.isAppLeftNavToggled,
  );
  const isMobile = useIsMobile();

  return (
    <HoverDiv $componentState={isActive ? ComponentStateEnum.ENABLED : state}>
      <RippleEffect
        shape={
          isMobile || !isAppLeftNavToggled
            ? RippleEffectShapeEnum.BUTTON
            : RippleEffectShapeEnum.RADIUS_16
        }
      >
        <AutoLayout
          $direction={AutoLayoutDirectionEnum.VERTICAL}
          $verticalResizing={AutoLayoutResizingEnum.HUG}
        >
          <ButtonAutoLayout
            $direction={AutoLayoutDirectionEnum.HORIZONTAL}
            $align={AutoLayoutAlignEnum.LEFT}
            $isRail={isAppLeftNavToggled}
            className="button"
          >
            <Icon icon={icon} color={ThemeColorEnum.ON_SECONDARY_CONTAINER} filled={isActive} />
            <div className="font-text-drawer">
              <FontText
                text={label}
                $color={ThemeColorEnum.ON_SECONDARY_CONTAINER}
                $fontType={FontTypeEnum.LABEL}
                $fontSize={FontSizeEnum.LARGE_PROMINENT}
              />
            </div>
          </ButtonAutoLayout>
          <DivRail $isRail={isAppLeftNavToggled}>
            <FontText
              text={label}
              $color={ThemeColorEnum.ON_SECONDARY_CONTAINER}
              $fontType={FontTypeEnum.LABEL}
              $fontSize={FontSizeEnum.MEDIUM_PROMINENT}
            />
          </DivRail>
        </AutoLayout>
      </RippleEffect>
    </HoverDiv>
  );
};

export default NavigationItemButton;
